/**
 * Zone IDF Synchronization
 *
 * Creates and updates Zone and BuildingSurface:Detailed IDF objects
 * from geometry editing operations.
 */

import type { IDFObject, IDFField } from '../../parser/types';
import type { SimpleBoxConfig, FootprintExtrudeConfig } from '../editingTypes';
import type { Vector3 } from '../types';
import {
  ZONE_FIELD_MAPPINGS,
  SURFACE_FIELD_MAPPINGS,
  BOUNDARY_CONDITIONS,
  surfaceTypeToIDF,
  getSunExposure,
  getWindExposure,
} from './fieldMappings';

/**
 * Result of creating zone IDF objects.
 */
export interface ZoneIDFResult {
  /** The Zone IDF object */
  zone: Omit<IDFObject, 'id'>;
  /** The surface IDF objects */
  surfaces: Array<Omit<IDFObject, 'id'>>;
  /** Total number of objects created */
  objectCount: number;
}

/**
 * Create a field object for IDF.
 */
function createField(value: string, comment?: string): IDFField {
  return {
    value,
    ...(comment && { inlineComment: `!- ${comment}` }),
  };
}

/**
 * Create Zone IDF object fields.
 */
function createZoneFields(
  name: string,
  origin: Vector3,
  directionOfRelativeNorth: number = 0
): IDFField[] {
  return [
    createField(name, ZONE_FIELD_MAPPINGS.NAME.name),
    createField(String(directionOfRelativeNorth), ZONE_FIELD_MAPPINGS.DIRECTION_OF_RELATIVE_NORTH.name),
    createField(String(origin.x), ZONE_FIELD_MAPPINGS.X_ORIGIN.name),
    createField(String(origin.y), ZONE_FIELD_MAPPINGS.Y_ORIGIN.name),
    createField(String(origin.z), ZONE_FIELD_MAPPINGS.Z_ORIGIN.name),
    createField('1', ZONE_FIELD_MAPPINGS.TYPE.name),
    createField('1', ZONE_FIELD_MAPPINGS.MULTIPLIER.name),
    createField('autocalculate', ZONE_FIELD_MAPPINGS.CEILING_HEIGHT.name),
    createField('autocalculate', ZONE_FIELD_MAPPINGS.VOLUME.name),
    createField('autocalculate', ZONE_FIELD_MAPPINGS.FLOOR_AREA.name),
  ];
}

/**
 * Create BuildingSurface:Detailed IDF object fields.
 */
function createSurfaceFields(
  name: string,
  surfaceType: string,
  construction: string,
  zoneName: string,
  boundaryCondition: string,
  boundaryObject: string,
  vertices: Vector3[]
): IDFField[] {
  const fields: IDFField[] = [
    createField(name, SURFACE_FIELD_MAPPINGS.NAME.name),
    createField(surfaceTypeToIDF(surfaceType), SURFACE_FIELD_MAPPINGS.SURFACE_TYPE.name),
    createField(construction, SURFACE_FIELD_MAPPINGS.CONSTRUCTION_NAME.name),
    createField(zoneName, SURFACE_FIELD_MAPPINGS.ZONE_NAME.name),
    createField('', SURFACE_FIELD_MAPPINGS.SPACE_NAME.name),
    createField(boundaryCondition, SURFACE_FIELD_MAPPINGS.OUTSIDE_BOUNDARY_CONDITION.name),
    createField(boundaryObject, SURFACE_FIELD_MAPPINGS.OUTSIDE_BOUNDARY_CONDITION_OBJECT.name),
    createField(getSunExposure(boundaryCondition), SURFACE_FIELD_MAPPINGS.SUN_EXPOSURE.name),
    createField(getWindExposure(boundaryCondition), SURFACE_FIELD_MAPPINGS.WIND_EXPOSURE.name),
    createField('autocalculate', SURFACE_FIELD_MAPPINGS.VIEW_FACTOR_TO_GROUND.name),
    createField(String(vertices.length), SURFACE_FIELD_MAPPINGS.NUMBER_OF_VERTICES.name),
  ];

  // Add vertices
  vertices.forEach((v, i) => {
    fields.push(createField(String(v.x), `Vertex ${i + 1} X-coordinate`));
    fields.push(createField(String(v.y), `Vertex ${i + 1} Y-coordinate`));
    fields.push(createField(String(v.z), `Vertex ${i + 1} Z-coordinate`));
  });

  return fields;
}

/**
 * Create IDF objects for a simple box zone.
 * Creates 1 Zone object and 6 BuildingSurface:Detailed objects.
 */
export function createSimpleBoxZoneIDFObjects(config: SimpleBoxConfig): ZoneIDFResult {
  const { name, origin, dimensions, constructions, directionOfRelativeNorth = 0 } = config;
  const { length, width, height } = dimensions;

  // Create Zone object
  const zone: Omit<IDFObject, 'id'> = {
    className: 'Zone',
    fields: createZoneFields(name, origin, directionOfRelativeNorth),
    leadingComments: [],
  };

  // Calculate vertices for all 6 surfaces
  // Origin is at the bottom-left-front corner of the box
  const x0 = origin.x;
  const y0 = origin.y;
  const z0 = origin.z;
  const x1 = origin.x + length;
  const y1 = origin.y + width;
  const z1 = origin.z + height;

  // Surface vertices (in counter-clockwise order when viewed from outside)
  const surfaces: Array<Omit<IDFObject, 'id'>> = [];

  // Floor (looking down from above, counter-clockwise)
  surfaces.push({
    className: 'BuildingSurface:Detailed',
    fields: createSurfaceFields(
      `${name} Floor`,
      'Floor',
      constructions.floor || 'Floor Construction',
      name,
      BOUNDARY_CONDITIONS.GROUND,
      '',
      [
        { x: x0, y: y0, z: z0 },
        { x: x0, y: y1, z: z0 },
        { x: x1, y: y1, z: z0 },
        { x: x1, y: y0, z: z0 },
      ]
    ),
    leadingComments: [],
  });

  // Ceiling/Roof (looking down from above, counter-clockwise)
  surfaces.push({
    className: 'BuildingSurface:Detailed',
    fields: createSurfaceFields(
      `${name} Ceiling`,
      'Ceiling',
      constructions.ceiling || 'Ceiling Construction',
      name,
      BOUNDARY_CONDITIONS.OUTDOORS,
      '',
      [
        { x: x0, y: y0, z: z1 },
        { x: x1, y: y0, z: z1 },
        { x: x1, y: y1, z: z1 },
        { x: x0, y: y1, z: z1 },
      ]
    ),
    leadingComments: [],
  });

  // South Wall (Y = y0, facing -Y, counter-clockwise from outside)
  surfaces.push({
    className: 'BuildingSurface:Detailed',
    fields: createSurfaceFields(
      `${name} South Wall`,
      'Wall',
      constructions.exteriorWalls || 'Exterior Wall Construction',
      name,
      BOUNDARY_CONDITIONS.OUTDOORS,
      '',
      [
        { x: x0, y: y0, z: z0 },
        { x: x1, y: y0, z: z0 },
        { x: x1, y: y0, z: z1 },
        { x: x0, y: y0, z: z1 },
      ]
    ),
    leadingComments: [],
  });

  // North Wall (Y = y1, facing +Y, counter-clockwise from outside)
  surfaces.push({
    className: 'BuildingSurface:Detailed',
    fields: createSurfaceFields(
      `${name} North Wall`,
      'Wall',
      constructions.exteriorWalls || 'Exterior Wall Construction',
      name,
      BOUNDARY_CONDITIONS.OUTDOORS,
      '',
      [
        { x: x1, y: y1, z: z0 },
        { x: x0, y: y1, z: z0 },
        { x: x0, y: y1, z: z1 },
        { x: x1, y: y1, z: z1 },
      ]
    ),
    leadingComments: [],
  });

  // East Wall (X = x1, facing +X, counter-clockwise from outside)
  surfaces.push({
    className: 'BuildingSurface:Detailed',
    fields: createSurfaceFields(
      `${name} East Wall`,
      'Wall',
      constructions.exteriorWalls || 'Exterior Wall Construction',
      name,
      BOUNDARY_CONDITIONS.OUTDOORS,
      '',
      [
        { x: x1, y: y0, z: z0 },
        { x: x1, y: y1, z: z0 },
        { x: x1, y: y1, z: z1 },
        { x: x1, y: y0, z: z1 },
      ]
    ),
    leadingComments: [],
  });

  // West Wall (X = x0, facing -X, counter-clockwise from outside)
  surfaces.push({
    className: 'BuildingSurface:Detailed',
    fields: createSurfaceFields(
      `${name} West Wall`,
      'Wall',
      constructions.exteriorWalls || 'Exterior Wall Construction',
      name,
      BOUNDARY_CONDITIONS.OUTDOORS,
      '',
      [
        { x: x0, y: y1, z: z0 },
        { x: x0, y: y0, z: z0 },
        { x: x0, y: y0, z: z1 },
        { x: x0, y: y1, z: z1 },
      ]
    ),
    leadingComments: [],
  });

  return {
    zone,
    surfaces,
    objectCount: 1 + surfaces.length,
  };
}

/**
 * Create IDF objects for a zone from an extruded footprint.
 * Creates 1 Zone object and N+2 BuildingSurface:Detailed objects
 * (N walls + floor + ceiling).
 */
export function createExtrudedZoneIDFObjects(config: FootprintExtrudeConfig): ZoneIDFResult {
  const { name, footprint, baseZ, height, constructions, directionOfRelativeNorth = 0 } = config;

  // Determine origin from footprint
  const minX = Math.min(...footprint.map((p) => p.x));
  const minY = Math.min(...footprint.map((p) => p.y));
  const origin: Vector3 = { x: minX, y: minY, z: baseZ };

  // Create Zone object
  const zone: Omit<IDFObject, 'id'> = {
    className: 'Zone',
    fields: createZoneFields(name, origin, directionOfRelativeNorth),
    leadingComments: [],
  };

  const surfaces: Array<Omit<IDFObject, 'id'>> = [];
  const topZ = baseZ + height;

  // Floor (counter-clockwise when viewed from above)
  const floorVertices: Vector3[] = footprint.map((p) => ({ x: p.x, y: p.y, z: baseZ }));
  surfaces.push({
    className: 'BuildingSurface:Detailed',
    fields: createSurfaceFields(
      `${name} Floor`,
      'Floor',
      constructions.floor || 'Floor Construction',
      name,
      BOUNDARY_CONDITIONS.GROUND,
      '',
      floorVertices
    ),
    leadingComments: [],
  });

  // Ceiling (clockwise when viewed from above = counter-clockwise from outside)
  const ceilingVertices: Vector3[] = [...footprint].reverse().map((p) => ({ x: p.x, y: p.y, z: topZ }));
  surfaces.push({
    className: 'BuildingSurface:Detailed',
    fields: createSurfaceFields(
      `${name} Ceiling`,
      'Ceiling',
      constructions.ceiling || 'Ceiling Construction',
      name,
      BOUNDARY_CONDITIONS.OUTDOORS,
      '',
      ceilingVertices
    ),
    leadingComments: [],
  });

  // Walls (one for each edge of the footprint)
  for (let i = 0; i < footprint.length; i++) {
    const p1 = footprint[i];
    const p2 = footprint[(i + 1) % footprint.length];
    if (!p1 || !p2) continue;

    // Wall vertices (counter-clockwise from outside)
    const wallVertices: Vector3[] = [
      { x: p1.x, y: p1.y, z: baseZ },
      { x: p2.x, y: p2.y, z: baseZ },
      { x: p2.x, y: p2.y, z: topZ },
      { x: p1.x, y: p1.y, z: topZ },
    ];

    surfaces.push({
      className: 'BuildingSurface:Detailed',
      fields: createSurfaceFields(
        `${name} Wall ${i + 1}`,
        'Wall',
        constructions.exteriorWalls || 'Exterior Wall Construction',
        name,
        BOUNDARY_CONDITIONS.OUTDOORS,
        '',
        wallVertices
      ),
      leadingComments: [],
    });
  }

  return {
    zone,
    surfaces,
    objectCount: 1 + surfaces.length,
  };
}

/**
 * Create IDF objects for a zone based on configuration.
 */
export function createZoneIDFObjects(
  config: { method: 'simple-box'; simpleBox: SimpleBoxConfig } | { method: 'extrude-footprint'; footprintExtrude: FootprintExtrudeConfig }
): ZoneIDFResult {
  if (config.method === 'simple-box') {
    return createSimpleBoxZoneIDFObjects(config.simpleBox);
  } else {
    return createExtrudedZoneIDFObjects(config.footprintExtrude);
  }
}

/**
 * Update Zone IDF object name.
 */
export function updateZoneName(
  zoneObject: IDFObject,
  newName: string
): IDFObject {
  const fields = [...zoneObject.fields];
  if (fields[ZONE_FIELD_MAPPINGS.NAME.index]) {
    fields[ZONE_FIELD_MAPPINGS.NAME.index] = {
      ...fields[ZONE_FIELD_MAPPINGS.NAME.index],
      value: newName,
    };
  }
  return { ...zoneObject, fields };
}

/**
 * Get zone name from IDF object.
 */
export function getZoneName(zoneObject: IDFObject): string {
  return zoneObject.fields[ZONE_FIELD_MAPPINGS.NAME.index]?.value ?? '';
}
